import React, { useCallback, useEffect, useMemo, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { handleClickForKeyboard } from "simplydo/core/accessibility";
import { Button, Divider } from "simplydo/react/ui";
import { Input } from "simplydo/react/ui";
import { Modal } from "simplydo/react/ui";

import type { DropZoneConfigProps } from "./FileChooser/DropZone";
import { FileUploaderPopup } from "./FileChooser/FileUploaderPopup";
import { allowedVideoTypesList } from "./FileChooser/uploadUtils";

type VideoChooserProps = {
  forType: string;
  forId: string;
  trigger?: React.ReactElement;
  content?: string;
  isBasic?: boolean;
  primary?: boolean;
  secondary?: boolean;
  disabled?: boolean;
  isOpen?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  onError?: (message: string) => void;
  onComplete: (
    responseFilename: string,
    filename: string,
    responseDownload: string,
  ) => void;
  dropzoneProps?: DropZoneConfigProps;
};

export const VideoChooser = ({
  forType,
  forId,
  isOpen,
  onOpen,
  onClose,
  onComplete,
  trigger,
  isBasic,
  primary,
  secondary,
  content,
  disabled,
  onError,
  dropzoneProps,
}: VideoChooserProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [link, setLink] = useState("");

  // Button handlers for manually opening and closing the chooser popup
  const closePopup = useCallback(() => {
    if (open && onClose) {
      onClose();
    }
    setOpen(false);
  }, [open, onClose]);

  const openPopup = useCallback(() => {
    if (open) {
      return closePopup();
    }
    if (!open && onOpen) onOpen();
    setOpen(true);
  }, [open, onOpen, closePopup]);

  const onChosen = (
    responseFilename: string,
    filename: string,
    responseDownload: string,
  ) => {
    onComplete(responseFilename, filename, responseDownload);
    closePopup();
  };

  useEffect(() => {
    if (isOpen !== undefined && open !== isOpen) {
      setOpen(isOpen);
    }
  }, [open, isOpen]);

  const updateVideoFromLink = () => {
    let storedLink;
    if (
      link.toLowerCase().indexOf("youtube") > -1 ||
      link.toLowerCase().indexOf("youtu.be") > -1
    ) {
      const regExp =
        /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|shorts\/|&v=)([^#&?]*).*/;
      const match = link.match(regExp);
      if (match?.[2] && match[2].length === 11) {
        storedLink = `embed-https://youtube.com/embed/${match[2]}`;
      } else if (onError) {
        return onError("This YouTube link is invalid");
      }
    } else if (link.toLowerCase().indexOf("vimeo.com") > -1) {
      const regExp =
        /^.*(player\.)*(vimeo\.com\/)((channels\/[A-z]+\/)|(groups\/[A-z]+\/videos\/)|(video\/))?([0-9]+)/;
      const match = link.match(regExp);
      if (match) {
        storedLink = `embed-https://player.vimeo.com/video/${match[7]}`;
      } else if (onError) {
        return onError("This Vimeo link is invalid");
      }
    } else if (link.length === 11) {
      storedLink = `embed-https://youtube.com/embed/${link}`;
    }
    if (!storedLink) {
      return onError?.("Unable to get embed link");
    }
    onChosen(storedLink, storedLink, storedLink);
    setLink("");
  };

  const triggerElement = useMemo(() => {
    if (trigger) {
      return React.cloneElement(trigger, {
        onClick: disabled ? () => {} : openPopup,
      } as React.HTMLAttributes<HTMLElement>);
    }

    return (
      <Button
        size="small"
        primary={primary}
        secondary={secondary}
        basic={isBasic}
        icon="video"
        disabled={disabled}
        content={content || t("ideas.actions.chooseVideo")}
        onClick={openPopup}
      />
    );
  }, [trigger, openPopup, disabled, isBasic, content, t, primary, secondary]);

  return (
    <>
      {triggerElement}
      <Modal
        mountNode={document.getElementById("semantic-modal-mount-node")}
        open={open}
      >
        <Modal.Header>{t("ideas.actions.chooseVideo")}</Modal.Header>
        <Modal.Content>
          <h3>{t("ideas.actions.chooseImage.info")}</h3>
          <FileUploaderPopup
            forType={forType}
            forId={forId}
            onComplete={onChosen}
            onError={toast.error}
            accept={allowedVideoTypesList.join(",")}
            dropzoneProps={dropzoneProps}
          />
          <Divider section />
          <h5 style={{ marginTop: 0 }}>Embed a video from YouTube or Vimeo</h5>
          <Input
            size="small"
            fluid
            icon="youtube"
            iconPosition="left"
            placeholder="Enter link to video..."
            onClick={(e: React.MouseEvent<HTMLInputElement>) => {
              (e.target as HTMLInputElement).focus();
            }}
            onKeyDown={handleClickForKeyboard(updateVideoFromLink, {
              keys: ["Enter"],
            })}
            onChange={(e) => setLink(e.target.value)}
            action={
              <Button
                size="tiny"
                primary
                content="Embed"
                onClick={updateVideoFromLink}
              />
            }
          />
        </Modal.Content>
        <Modal.Actions>
          <Button content={t("generic.close")} onClick={closePopup} />
        </Modal.Actions>
      </Modal>
    </>
  );
};
